import { useRef } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useReducedMotion,
} from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";

export const Hero = () => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });

    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
    const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
    const titleOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
    const gridOpacity = useTransform(scrollYProgress, [0, 1], [0.4, 0.05]);
    // bgY/bgScale are intentionally unused after 3D scene replaces hero image
    void bgY;
    void bgScale;

    const headlineParts = [
        "Global Trade.",
        "Trusted Supply Chains.",
        "Sustainable Growth.",
    ];

    return (
        <section
            ref={ref}
            id="hero"
            data-testid="hero-section"
            className="relative min-h-[100vh] w-full overflow-hidden"
        >
            {/* 3D scene shows through behind. Soft vignette only. */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_30%,#0B0807_95%)]"
            />

            {/* Grid overlay */}
            <motion.div
                aria-hidden
                style={{ opacity: reduce ? 0.1 : gridOpacity }}
                className="absolute inset-0 pointer-events-none"
            >
                <div
                    className="w-full h-full"
                    style={{
                        backgroundImage:
                            "linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)",
                        backgroundSize: "80px 80px",
                    }}
                />
            </motion.div>

            {/* Top label strip */}
            <div className="relative z-10 pt-28 sm:pt-32 px-4 sm:px-8 lg:px-12">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono-text text-[10px] sm:text-xs uppercase tracking-[0.25em] text-zinc-400">
                    <span className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-[#D69331] animate-pulse" />
                        Kuala Lumpur · Malaysia
                    </span>
                    <span className="hidden sm:inline">/</span>
                    <span>International FMCG Trading</span>
                    <span className="hidden sm:inline">/</span>
                    <span>Halal Supply Chain</span>
                    <span className="hidden sm:inline">/</span>
                    <span>Import · Export</span>
                </div>
            </div>

            {/* Headline */}
            <motion.div
                style={reduce ? {} : { y: titleY, opacity: titleOpacity }}
                className="relative z-10 px-4 sm:px-8 lg:px-12 pt-10 sm:pt-16"
            >
                <h1
                    data-testid="hero-headline"
                    className="font-display font-bold uppercase leading-[0.85] tracking-[-0.03em] text-white text-[11vw] sm:text-[10vw] lg:text-[9vw] xl:text-[8.5rem] drop-shadow-[0_4px_30px_rgba(0,0,0,0.55)]"
                >
                    {headlineParts.map((line, i) => (
                        <motion.span
                            key={line}
                            initial={{ y: "110%", opacity: 0 }}
                            animate={{ y: "0%", opacity: 1 }}
                            transition={{
                                duration: 0.9,
                                ease: [0.16, 1, 0.3, 1],
                                delay: 0.1 + i * 0.12,
                            }}
                            className="block overflow-hidden"
                        >
                            <span className="inline-block">
                                {i === 1 ? (
                                    <>
                                        <span className="text-[#D69331]">
                                            Trusted
                                        </span>{" "}
                                        Supply Chains.
                                    </>
                                ) : (
                                    line
                                )}
                            </span>
                        </motion.span>
                    ))}
                </h1>
            </motion.div>

            {/* Sub + CTA row */}
            <div className="relative z-10 px-4 sm:px-8 lg:px-12 mt-12 lg:mt-16 pb-24">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
                    <motion.p
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.7, duration: 0.7 }}
                        className="md:col-span-6 lg:col-span-5 font-mono-text text-sm sm:text-base text-zinc-300 leading-relaxed"
                    >
                        Connecting manufacturers, distributors and markets
                        across{" "}
                        <span className="text-white">
                            Asia, the Middle East, Oceania
                        </span>{" "}
                        and{" "}
                        <span className="text-white">North America</span> — an
                        asset-light, scalable trading ecosystem built from
                        Malaysia.
                    </motion.p>

                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.85, duration: 0.7 }}
                        className="md:col-span-6 lg:col-span-7 flex flex-wrap gap-3 lg:justify-end"
                    >
                        <a
                            href="#services"
                            data-testid="hero-cta-services"
                            className="group inline-flex items-center gap-3 px-7 py-4 bg-[#D69331] text-black font-mono-text text-xs font-bold uppercase tracking-[0.25em] hover:bg-white transition-colors"
                        >
                            Explore Our Services
                            <ArrowRight
                                size={16}
                                className="group-hover:translate-x-1 transition-transform"
                            />
                        </a>
                        <a
                            href="#contact"
                            data-testid="hero-cta-contact"
                            className="group inline-flex items-center gap-3 px-7 py-4 border border-white/30 text-white font-mono-text text-xs font-bold uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-colors"
                        >
                            Contact Our Team
                            <ArrowRight
                                size={16}
                                className="group-hover:translate-x-1 transition-transform"
                            />
                        </a>
                    </motion.div>
                </div>

                {/* Scroll indicator */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.4, duration: 0.6 }}
                    className="mt-20 flex items-center justify-between border-t border-white/10 pt-6"
                >
                    <span className="font-mono-text text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                        Scroll · 001 / 12
                    </span>
                    <motion.div
                        animate={
                            reduce ? {} : { y: [0, 6, 0] }
                        }
                        transition={{
                            duration: 1.8,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        className="flex items-center gap-2 text-zinc-400"
                    >
                        <ArrowDown size={14} />
                        <span className="font-mono-text text-[10px] uppercase tracking-[0.3em]">
                            Continue
                        </span>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};
